/**
 * Ydim internal analytics (POST /api/analytics/track).
 * Reuses the same localStorage session id as visit tracking (sessionUtils).
 */

import { getSessionId } from '../utils/sessionUtils';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';

export { getSessionId };

/**
 * @param {string} eventType
 * @param {{ page?: string, metadata?: Record<string, unknown>, referrer?: string }} [options]
 */
export function trackEvent(eventType, options = {}) {
  if (!eventType || typeof eventType !== 'string') {
    return Promise.resolve();
  }
  const page =
    options.page != null
      ? String(options.page)
      : typeof window !== 'undefined'
        ? window.location.pathname
        : '';
  const metadata =
    options.metadata && typeof options.metadata === 'object' && !Array.isArray(options.metadata)
      ? options.metadata
      : {};
  const referrer =
    options.referrer != null
      ? String(options.referrer)
      : typeof document !== 'undefined'
        ? (document.referrer || '')
        : '';

  const body = JSON.stringify({
    event_type: eventType,
    page: page || null,
    session_id: getSessionId(),
    metadata,
    referrer,
  });

  const url = buildApiUrl(API_ENDPOINTS.ANALYTICS_TRACK);
  const init = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body,
    keepalive: true,
  };

  if (typeof fetch === 'undefined') {
    return Promise.resolve();
  }

  return fetch(url, init)
    .catch(() => {})
    .then(() => {});
}

const SS_VISITED_PATHS = 'ydim_analytics_visited_paths';
const SS_ADMIN_VISIT = 'ydim_analytics_admin_first_visit';

function readVisitedPaths() {
  try {
    const raw = sessionStorage.getItem(SS_VISITED_PATHS);
    if (!raw) return new Set();
    const a = JSON.parse(raw);
    return new Set(Array.isArray(a) ? a : []);
  } catch {
    return new Set();
  }
}

function writeVisitedPaths(set) {
  try {
    sessionStorage.setItem(SS_VISITED_PATHS, JSON.stringify([...set]));
  } catch {
    /* private mode or quota */
  }
}

function hasAdminVisitBeenRecorded() {
  try {
    return sessionStorage.getItem(SS_ADMIN_VISIT) === '1';
  } catch {
    return false;
  }
}

function markAdminVisitRecorded() {
  try {
    sessionStorage.setItem(SS_ADMIN_VISIT, '1');
  } catch {
    /* */
  }
}

/**
 * Heuristic route analytics (SPA + full reload). Does not count /admin in page_view; uses admin_* types for /admin.
 * @param {string} pathname - current location pathname
 */
export function trackRouteNavigation(pathname) {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  const p = pathname != null ? String(pathname) : window.location.pathname || '/';
  const isAdmin = p === '/admin' || p.startsWith('/admin/');

  let nav;
  try {
    const list = performance.getEntriesByType('navigation');
    nav = list && list[0] ? list[0] : null;
  } catch {
    nav = null;
  }
  const isReload = !!(nav && nav.type === 'reload');

  if (isReload) {
    if (isAdmin) {
      return trackEvent('admin_refresh', { page: p, metadata: { path: p } });
    }
    return trackEvent('page_refresh', { page: p, metadata: { path: p } });
  }

  if (isAdmin) {
    if (!hasAdminVisitBeenRecorded()) {
      markAdminVisitRecorded();
      return trackEvent('admin_visit', { page: p, metadata: { path: p } });
    }
    return Promise.resolve();
  }

  const visited = readVisitedPaths();
  if (visited.has(p)) {
    return Promise.resolve();
  }
  visited.add(p);
  writeVisitedPaths(visited);
  return trackEvent('page_view', { page: p, metadata: { path: p } });
}

/**
 * @param {string} path
 * @deprecated Prefer trackRouteNavigation from RouteTracker; kept for compatibility.
 */
export function trackPageView(path) {
  return trackRouteNavigation(path);
}

/**
 * @param {string} buttonName
 * @param {string} [page]
 * @param {Record<string, unknown>} [extra]
 */
export function trackClick(buttonName, page, extra = {}) {
  return trackEvent('button_click', {
    page: page != null ? page : typeof window !== 'undefined' ? window.location.pathname : undefined,
    metadata: { buttonName, ...extra },
  });
}
